// <div data-controller="business--plan-with-credit">
//
// </div>
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import * as helpers from "@/utils/helpers";

// https://stimulus.hotwired.dev/reference
export default class extends Controller {
  static targets = [
      "planTypeInput", "creditPlanSelect", "paymentCycleInput",
      "priceDescription", "extraCreditDescription", "referralCouponDiscount",
      "cleanUpAgreement",
      "cleanUpAgreementCheckBox",
      "brandingPlanFeature",
      "marketingPlanFeature",
      "trialCreditsReset",
      "trialCreditsResetAgreementCheckBox",
      "businessAgreement",
      "businessAgreementCheckBox",
      "legacyExtensionAgreement",
      "legacyExtensionAgreementCheckBox",
      "legacyExtensionCurrentPrice",
      "legacyExtensionFuturePrice",
      "legacyExtensionMessageLegacy",
      "legacyExtensionMessageCurrent",
      "immediateApply",
      "immediateApplyCheckBox",
      "immediateApplyMessage",
      "immediateApplyLabel"
  ];
  static classes = [ "" ]
  static values = {
    planTypeCreditPlanOptions: Object,
    creditPlanMapping: Object,
    yearlyRate: Number,
    currentCreditPlanId: Number,
    persistedPlanType: String,
    persistedPaymentCycle: String,
    isPersisted: Boolean,
    subscriptionPlanType: String,
    isTrialPeriod: Boolean,
    referralCoupon: Object,
    previewUrl: String,
    hasActivePayment: Boolean,
    legacyGraceEndsAt: String,
    legacyGraceEndsAtIso: String,
    subscriptionEndsAtIso: String
  };

  initialize() {
    this.currentPreview = null;
    this.previewRequestToken = 0;
  }

  get currentPlanType() {
    let checkedPlan;
    this.planTypeInputTargets.forEach((target) => {
      if (target.checked) {
        checkedPlan = target.value;
      }
    });
    return checkedPlan;
  }

  get currentPaymentCycle() {
    let checkedPaymentCycle;
    this.paymentCycleInputTargets.forEach((target) => {
      if (target.checked) {
        checkedPaymentCycle = target.value;
      }
    });
    return checkedPaymentCycle;
  }

  get currentCreditPlan() {
    const creditPlanOptions = this.creditPlanSelectTarget.options;
    const selectedCreditPlanId = creditPlanOptions[creditPlanOptions.selectedIndex].value;
    return this.creditPlanMappingValue[selectedCreditPlanId];
  }

  hasValidReferralCoupon() {
    return this.referralCouponValue && 
           this.referralCouponValue.code && 
           this.referralCouponValue.discount_type && 
           this.referralCouponValue.discount_value;
  }

  updateCreditPlanSelectOptions(options) {
    // clear previous options
    while (this.creditPlanSelectTarget.options.length > 0) {
      this.creditPlanSelectTarget.remove(0);
    }

    let hasMatchingDefaultOption;
    options.forEach((option) => {
      const newOption = document.createElement('option');
      const optionText = document.createTextNode(option.text);
      newOption.appendChild(optionText);
      newOption.setAttribute('value', option.value);
      if (this.currentCreditPlanIdValue === option.value) {
        hasMatchingDefaultOption = true;
      }
      this.creditPlanSelectTarget.appendChild(newOption);
    })

    if (hasMatchingDefaultOption) {
      this.creditPlanSelectTarget.value = this.currentCreditPlanIdValue;
    }
  }

  warnPlanTypeDowngrade(show, targetPlan) {
    if (this.subscriptionPlanTypeValue === "branding") {
      if (targetPlan === "basic") {
        this.brandingPlanFeatureTargets.forEach((target) => target.classList.remove("hidden"));
        this.marketingPlanFeatureTargets.forEach((target) => target.classList.add("hidden"));
      }
    } else if (this.subscriptionPlanTypeValue === "marketing") {
      if (targetPlan === "basic") {
        this.brandingPlanFeatureTargets.forEach((target) => target.classList.remove("hidden"));
        this.marketingPlanFeatureTargets.forEach((target) => target.classList.remove("hidden"));
      } else if (targetPlan === "branding") {
        this.brandingPlanFeatureTargets.forEach((target) => target.classList.add("hidden"));
        this.marketingPlanFeatureTargets.forEach((target) => target.classList.remove("hidden"));
      }
    }

    if (this.hasCleanUpAgreementTarget) {
      this.cleanUpAgreementTarget.classList.toggle("hidden", !show)
      this.cleanUpAgreementCheckBoxTarget.required = show;
    }
  }

  warnTrialPeriodFeature(show) {
    if (this.hasTrialCreditsResetTarget) {
      this.trialCreditsResetTarget.classList.toggle("hidden", !show)
      this.trialCreditsResetAgreementCheckBoxTarget.required = show;
    }
  }

  afterPlanTypeSelected() {
    if (this.currentPlanType === "basic") {
      this.updateCreditPlanSelectOptions(this.planTypeCreditPlanOptionsValue.basic);
      this.warnPlanTypeDowngrade(this.isPersistedValue && (this.subscriptionPlanTypeValue !== "basic"), "basic");
    } else if (this.currentPlanType === "branding") {
      this.updateCreditPlanSelectOptions(this.planTypeCreditPlanOptionsValue.branding);
      this.warnPlanTypeDowngrade(this.isPersistedValue && (this.subscriptionPlanTypeValue !== "basic" && this.subscriptionPlanTypeValue !== "branding"), "branding");
    } else if (this.currentPlanType === "marketing") {
      this.updateCreditPlanSelectOptions(this.planTypeCreditPlanOptionsValue.marketing);
      this.warnPlanTypeDowngrade(false);
    }

    this.warnTrialPeriodFeature(this.isTrialPeriodValue && (this.subscriptionPlanTypeValue === "basic") && (this.currentPlanType !== "basic"));
    this.calculatePrice();
    this.refreshUpgradeBonusPreview();
    this.toggleLegacyExtensionAgreement();
  }

  afterPaymentCycleSelected() {
    this.calculatePrice();
    this.refreshUpgradeBonusPreview();
    this.toggleLegacyExtensionAgreement();
  }

  afterCreditPlanSelected() {
    this.calculatePrice();
    this.refreshUpgradeBonusPreview();
    this.toggleLegacyExtensionAgreement();
  }

  toggleLegacyExtensionAgreement() {
    if (!this.hasLegacyExtensionAgreementTarget) return;
    const plan = this.currentCreditPlan;
    if (!plan) {
      this.legacyExtensionAgreementTarget.classList.add("hidden");
      this.legacyExtensionAgreementCheckBoxTarget.required = false;
      this.legacyExtensionAgreementCheckBoxTarget.checked = false;
      return;
    }
    // 초기 상태(현재 결제 중인 그대로)라면 숨김. credit_plan 또는 payment_cycle 이 바뀐 경우에만 노출.
    // legacy / current 어느 plan 으로 변경하든 명시적 동의 체크박스 필수 (묵시적 동의 방지).
    const isUnchanged =
      Number(plan.id) === Number(this.currentCreditPlanIdValue) &&
      this.currentPaymentCycle === this.persistedPaymentCycleValue;
    const shouldShow = !isUnchanged;
    this.legacyExtensionAgreementTarget.classList.toggle("hidden", !shouldShow);
    this.legacyExtensionAgreementCheckBoxTarget.required = shouldShow;
    if (!shouldShow) {
      this.legacyExtensionAgreementCheckBoxTarget.checked = false;
      return;
    }

    // legacy 선택 vs current 선택에 따라 안내 메시지 토글.
    const isLegacy = plan.legacy === true;
    if (this.hasLegacyExtensionMessageLegacyTarget) {
      this.legacyExtensionMessageLegacyTarget.classList.toggle("hidden", !isLegacy);
    }
    if (this.hasLegacyExtensionMessageCurrentTarget) {
      this.legacyExtensionMessageCurrentTarget.classList.toggle("hidden", isLegacy);
    }

    if (isLegacy) {
      const isYearly = this.currentPaymentCycle === "yearly";
      const multiplier = isYearly ? this.yearlyRateValue : 1;
      if (this.hasLegacyExtensionCurrentPriceTarget) {
        this.legacyExtensionCurrentPriceTarget.innerText = helpers.krw(plan.price * multiplier);
      }
      if (this.hasLegacyExtensionFuturePriceTarget) {
        this.legacyExtensionFuturePriceTarget.innerText =
          plan.future_price ? helpers.krw(plan.future_price * multiplier) : "(안내 예정)";
      }
    }
  }

  paymentCycleAvailability(unitPrice) {
    this.paymentCycleInputTargets.forEach((target) => {
      target.disabled = (unitPrice === 0 && (target.value !== "monthly"));
      const targetLabel = document.querySelector(`label[for=${target.id}`)
      targetLabel.classList.toggle("disabled", target.disabled)

      if (unitPrice === 0 && (target.value === "monthly")) {
        target.checked = true;
      }
    })
  }

  toggleBusinessAgreement(show) {
    if (this.hasBusinessAgreementTarget) {
      this.businessAgreementTarget.classList.toggle("hidden", !show)
      this.businessAgreementCheckBoxTarget.checked = false;
      this.businessAgreementCheckBoxTarget.required = show;
    }
  }

  calculatePrice() {
    if (this.currentCreditPlan === undefined || this.currentPaymentCycle === undefined) {
      this.priceDescriptionTarget.innerHTML = "-";
      this.hideReferralCouponDiscount();
      return;
    }

    let label;
    const unitPrice = this.currentCreditPlan.price;
    const credits = this.currentCreditPlan.credits;

    this.toggleBusinessAgreement(unitPrice > 0);
    this.paymentCycleAvailability(unitPrice);

    let finalPrice = unitPrice;
    if (this.currentPaymentCycle === "yearly") {
      finalPrice = this.yearlyRateValue * unitPrice;
    }

    // Calculate referral coupon discount
    let referralDiscount = 0;
    if (this.hasValidReferralCoupon() && finalPrice > 0) {
      if (this.referralCouponValue.discount_type === "percentage") {
        referralDiscount = Math.round(finalPrice * this.referralCouponValue.discount_value / 100);
      } else {
        referralDiscount = Math.min(this.referralCouponValue.discount_value, finalPrice);
      }
    }

    if (this.currentPaymentCycle === "yearly") {
      const originalPrice = this.yearlyRateValue * unitPrice;
      const discountPrice = (12 - this.yearlyRateValue) * unitPrice;
      const discountLabel = `<span class="italic text-xs text-red-600 dark:text-red-300">${helpers.krw(discountPrice)} 연간 결제 할인 적용</span>`;
      
      if (referralDiscount > 0) {
        const discountedPrice = originalPrice - referralDiscount;
        label = `<div>
            <div>첫 해: 연 ${helpers.numberWithDelimiter(credits * 12)}건 (${helpers.krw(discountedPrice)}) ${discountLabel}</div>
            <div class="text-sm text-gray-600 dark:text-gray-400 mt-1">다음 해부터: 연 ${helpers.numberWithDelimiter(credits * 12)}건 (${helpers.krw(originalPrice)}) ${discountLabel}</div>
          </div>`;
      } else {
        label = `연 ${helpers.numberWithDelimiter(credits * 12)}건 (${helpers.krw(originalPrice)}) ${discountLabel}`;
      }
    } else if (this.currentPaymentCycle === "monthly") {
      if (finalPrice === 0) {
        label = this.currentCreditPlan.price_description;
      } else if (referralDiscount > 0) {
        const discountedPrice = finalPrice - referralDiscount;
        label = `<div>
            <div>첫 달: 월 ${helpers.numberWithDelimiter(credits)}건 (${helpers.krw(discountedPrice)})</div>
            <div class="text-sm text-gray-600 dark:text-gray-400 mt-1">다음 달부터: 월 ${helpers.numberWithDelimiter(credits)}건 (${helpers.krw(finalPrice)})</div>
          </div>`;
      } else {
        label = `월 ${helpers.numberWithDelimiter(credits)}건 (${helpers.krw(finalPrice)})`;
      }
    }

    // Show referral coupon discount if applicable
    if (referralDiscount > 0) {
      this.showReferralCouponDiscount();
    } else {
      this.hideReferralCouponDiscount();
    }

    // 유예기간 동안 legacy plan 으로 결제 중인 사용자에게 인상 후 가격 안내.
    // 결제 주기에 맞춰 환산 (yearly = future_price × yearlyRate).
    const futureUnitPrice = this.currentCreditPlan.future_price;
    if (this.currentCreditPlan.legacy && futureUnitPrice && futureUnitPrice !== unitPrice) {
      const futureFinalPrice = this.currentPaymentCycle === "yearly"
        ? this.yearlyRateValue * futureUnitPrice
        : futureUnitPrice;
      const graceEndsAt = this.legacyGraceEndsAtValue;
      label += `<div class="text-xs text-amber-700 dark:text-amber-300 mt-1"><b>${graceEndsAt}</b> 이후에는 인상된 금액인 <b>${helpers.krw(futureFinalPrice)}</b>으로 결제됩니다.</div>`;
    }

    this.extraCreditDescriptionTarget.innerText = `이후 초과되는 발송량에 대해서는 500건 기준 ${helpers.krw(Math.round(this.currentCreditPlan.extra_credit_price * 500))} 으로 추가 충전이 필요 합니다.`;
    this.priceDescriptionTarget.innerHTML = label;
  }

  showReferralCouponDiscount() {
    if (this.hasReferralCouponDiscountTarget && this.hasValidReferralCoupon()) {
      this.referralCouponDiscountTarget.innerHTML = `🎉 [${this.referralCouponValue.name}] 첫 결제 할인: ${this.referralCouponValue.display_discount} 할인 적용됩니다`;;
      this.referralCouponDiscountTarget.classList.remove("hidden");
    }
  }

  hideReferralCouponDiscount() {
    if (this.hasReferralCouponDiscountTarget) {
      this.referralCouponDiscountTarget.classList.add("hidden");
    }
  }

  async refreshUpgradeBonusPreview() {
    if (!this.hasImmediateApplyTarget) return;
    if (!this.hasActivePaymentValue) return this.hideImmediateApply();
    if (!this.currentPlanType || !this.currentPaymentCycle) return this.hideImmediateApply();

    const select = this.creditPlanSelectTarget;
    if (!select || select.options.length === 0 || !select.value) return this.hideImmediateApply();

    const myToken = ++this.previewRequestToken;
    try {
      const resp = await api.get(this.previewUrlValue, {
        plan_type: this.currentPlanType,
        credit_plan_id: select.value,
        payment_cycle: this.currentPaymentCycle
      });
      if (myToken !== this.previewRequestToken) return;
      this.applyPreview(resp.data);
    } catch (e) {
      if (myToken === this.previewRequestToken) this.hideImmediateApply();
    }
  }

  applyPreview(preview) {
    this.currentPreview = preview;

    if (!preview || preview.change_type === "unchanged" || preview.already_reserved) {
      this.hideImmediateApply();
      return;
    }

    this.immediateApplyTarget.classList.remove("hidden");

    if (preview.change_type === "upgrade") {
      const bonusCredits = Number(preview.bonus_credits || 0);
      if (bonusCredits <= 0) {
        this.hideImmediateApply();
        return;
      }
      const bonus = bonusCredits.toLocaleString();
      this.immediateApplyMessageTarget.innerHTML =
        `기다리지 않고 바로 새 요금제로 변경할 수도 있어요. ` +
        `지금 즉시 적용을 선택하시면 보너스로 <b>${bonus}건</b> 발송량을 추가로 드려요. ` +
        `<span class="text-xs text-slate-500 dark:text-slate-400">(현재 요금제의 남은 이용 기간 및 잔여 발송량 기반)</span>`;
      this.immediateApplyLabelTarget.textContent = "지금 바로 새 요금제 적용하고 보너스 발송량 받기";
    } else {
      let baseMessage =
        `기본적으로 다음 결제 시점에 새 요금제가 적용돼요. ` +
        `원하시면 기존 요금제를 지금 만료 처리하고 새 요금제를 바로 적용할 수도 있어요. ` +
        `<span class="text-xs text-slate-500 dark:text-slate-400">(이 경우 현재 <span class="text-red-600 dark:text-red-400 font-medium">남은 잔여 발송량은 소멸</span>되니 참고해 주세요)</span>`;

      // legacy plan + 연간 결제 + 다음 결제일이 유예 기간 종료 후 → 즉시 적용 권장.
      // 그대로 두면 다음 결제(=유예 후) 시점에 신규 가격으로 결제되어 연간 락인 효과가 사라짐.
      const plan = this.currentCreditPlan;
      const isLegacyYearlyPostGraceRenewal = plan && plan.legacy &&
        this.currentPaymentCycle === "yearly" &&
        this.subscriptionEndsAtIsoValue && this.legacyGraceEndsAtIsoValue &&
        new Date(this.subscriptionEndsAtIsoValue) >= new Date(this.legacyGraceEndsAtIsoValue);

      if (isLegacyYearlyPostGraceRenewal) {
        const graceLabel = this.legacyGraceEndsAtValue;
        baseMessage =
          `<span class="text-amber-700 dark:text-amber-300 font-medium">현재 결제 주기가 유예 기간 종료일(${graceLabel}) 이후에 만료될 예정이라, 그대로 두시면 다음 결제 시점에는 신규 요금제가 적용돼요.</span> ` +
          `기존 가격으로 1년간 락인하시려면 <b>유예 기간 종료 전(${graceLabel}까지)</b>에 즉시 적용으로 결제를 진행해 주세요. ` +
          `지금 즉시 적용하지 않으시더라도 일단 저장하시고, 유예 기간 종료 전에 다시 들어오셔서 즉시 적용을 진행하시면 됩니다. ` +
          `<span class="text-xs text-slate-500 dark:text-slate-400">(즉시 적용 시 현재 <span class="text-red-600 dark:text-red-400 font-medium">남은 잔여 발송량은 소멸</span>되니 참고해 주세요)</span>`;
      }

      this.immediateApplyMessageTarget.innerHTML = baseMessage;
      this.immediateApplyLabelTarget.textContent = "기존 요금제 만료하고 새 요금제 즉시 적용하기";
    }

    this.syncSubmitButtonLabel();
  }

  hideImmediateApply() {
    if (!this.hasImmediateApplyTarget) return;
    this.immediateApplyTarget.classList.add("hidden");
    if (this.hasImmediateApplyCheckBoxTarget) {
      this.immediateApplyCheckBoxTarget.checked = false;
    }
    this.currentPreview = null;
    this.syncSubmitButtonLabel();
  }

  afterImmediateApplyToggled() {
    this.syncSubmitButtonLabel();
  }

  syncSubmitButtonLabel() {
    const btn = document.getElementById("planChangeSubmitButton");
    if (!btn) return;
    const defaultText = btn.dataset.defaultText || "요금제 변경하기";
    const checked = this.hasImmediateApplyCheckBoxTarget && this.immediateApplyCheckBoxTarget.checked;
    const cp = this.currentPreview;
    if (checked && cp && (cp.change_type === "upgrade" || cp.change_type === "downgrade")) {
      btn.textContent = "새로운 요금제 즉시 적용하기";
    } else if (cp && cp.change_type !== "unchanged") {
      btn.textContent = "요금제 변경 예약하기";
    } else {
      btn.textContent = defaultText;
    }
  }

  connect() {
    this.afterPlanTypeSelected();
  }

  disconnect() {

  }
}
